import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Package,
  Truck,
  Wheat,
  Store,
  BarChart3,
  Wrench,
  FileStack,
  ArrowRight,
  Circle,
  Plus,
  Upload,
  ClipboardCheck,
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
  User,
  Settings,
  Factory,
  DollarSign,
  ShoppingCart,
  Activity,
} from "lucide-react";
import { api } from "../api";
import { useUser } from "../contexts/UserContext";
import { PageHero } from "../components/PageHero";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function useNow() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function getGreeting(hour: number) {
  if (hour < 12) return "早安";
  if (hour < 18) return "午安";
  return "晚安";
}

function formatDate(d: Date) {
  const weekdays = ["日", "一", "二", "三", "四", "五", "六"];
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")} 星期${weekdays[d.getDay()]}`;
}

function formatTime(d: Date) {
  return d.toLocaleTimeString("zh-TW", { hour: "2-digit", minute: "2-digit" });
}

function relativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "剛剛";
  if (mins < 60) return `${mins} 分鐘前`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} 小時前`;
  return `${Math.floor(hrs / 24)} 天前`;
}

// ---------------------------------------------------------------------------
// Raw fetch for non-v1 endpoints
// ---------------------------------------------------------------------------

async function rawFetch<T>(url: string): Promise<T> {
  const res = await fetch(url, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

// ---------------------------------------------------------------------------
// Action Required Card
// ---------------------------------------------------------------------------

type ActionSeverity = "red" | "yellow" | "blue" | "gray";

const severityStyles: Record<ActionSeverity, { border: string; bg: string; dot: string; text: string }> = {
  red:    { border: "border-l-red-500",    bg: "bg-red-50/60",    dot: "fill-red-500 text-red-500",       text: "text-red-700" },
  yellow: { border: "border-l-amber-400",  bg: "bg-amber-50/60",  dot: "fill-amber-400 text-amber-400",   text: "text-amber-700" },
  blue:   { border: "border-l-blue-500",   bg: "bg-blue-50/60",   dot: "fill-blue-500 text-blue-500",     text: "text-blue-700" },
  gray:   { border: "border-l-slate-400",  bg: "bg-slate-50/60",  dot: "fill-slate-400 text-slate-400",   text: "text-slate-600" },
};

function ActionCard({
  severity,
  icon: Icon,
  label,
  count,
  to,
  delay,
}: {
  severity: ActionSeverity;
  icon: React.ElementType;
  label: string;
  count: number | undefined;
  to: string;
  delay: number;
}) {
  const s = severityStyles[severity];
  const resolved = count !== undefined && count === 0;

  return (
    <Link
      to={to}
      className={`group flex items-center gap-3 rounded-lg border border-slate-200/80 border-l-[3px] ${s.border} ${resolved ? "bg-white" : s.bg} px-4 py-3 no-underline transition-all hover:shadow-sm animate-fade-up`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${resolved ? "bg-signal-400/10 text-signal-500" : `${s.bg} ${s.text}`}`}>
        {resolved ? <CheckCircle2 size={18} strokeWidth={1.8} /> : <Icon size={18} strokeWidth={1.8} />}
      </div>
      <div className="flex-1 min-w-0">
        <span className={`text-sm font-medium ${resolved ? "text-slate-400" : "text-slate-700"}`}>
          {label}
        </span>
      </div>
      {resolved ? (
        <span className="text-xs text-signal-500 font-medium whitespace-nowrap">✓ 無待處理</span>
      ) : (
        <div className="flex items-center gap-2">
          <span className={`text-lg font-bold tabular-nums ${s.text}`}>
            {count ?? "—"}
          </span>
          <ArrowRight size={14} className="text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-slate-500" />
        </div>
      )}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Quick Action Button
// ---------------------------------------------------------------------------

function QuickAction({
  icon: Icon,
  label,
  to,
  delay,
}: {
  icon: React.ElementType;
  label: string;
  to: string;
  delay: number;
}) {
  return (
    <Link
      to={to}
      className="group flex flex-col items-center gap-1.5 rounded-xl border border-slate-200/60 bg-white px-3 py-3 no-underline transition-all hover:border-slate-300 hover:shadow-sm animate-fade-up"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-500 transition-colors group-hover:bg-qimei-50 group-hover:text-qimei-500">
        <Icon size={18} strokeWidth={1.5} />
      </div>
      <span className="text-xs font-medium text-slate-600">{label}</span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Mini stat card
// ---------------------------------------------------------------------------

function MiniStat({
  label,
  value,
  icon: Icon,
  to,
  delay,
}: {
  label: string;
  value: number | undefined;
  icon: React.ElementType;
  to: string;
  delay: number;
}) {
  return (
    <Link
      to={to}
      className="flex flex-col items-center justify-center rounded-xl border border-slate-200/60 bg-white p-4 animate-fade-up no-underline transition-all hover:border-slate-300 hover:shadow-sm"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-400 mb-2">
        <Icon size={16} strokeWidth={1.5} />
      </div>
      <p
        className="text-2xl font-bold tracking-tight text-slate-900 animate-count-in"
        style={{ animationDelay: `${delay + 150}ms`, fontVariantNumeric: "tabular-nums" }}
      >
        {value ?? "—"}
      </p>
      <p className="text-[11px] font-medium text-slate-400 mt-0.5">{label}</p>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// System status
// ---------------------------------------------------------------------------

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <Circle
      size={7}
      className={ok ? "fill-signal-400 text-signal-400" : "fill-danger-400 text-danger-400"}
    />
  );
}

// ---------------------------------------------------------------------------
// Audit log entry
// ---------------------------------------------------------------------------

interface AuditEntry {
  id: number;
  user_email: string | null;
  action: string;
  resource_type: string;
  resource_name: string | null;
  created_at: string;
}

function actionIcon(action: string) {
  if (action === "create") return Plus;
  if (action === "update") return Settings;
  if (action === "delete") return AlertTriangle;
  return FileText;
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export function Dashboard() {
  const now = useNow();
  const { user } = useUser();

  // KPI counts
  const products = useQuery({
    queryKey: ["products-count"],
    queryFn: () => api.listProducts({ per_page: 1 }),
  });
  const suppliers = useQuery({
    queryKey: ["suppliers-count"],
    queryFn: () => api.listSuppliers({ per_page: 1 }),
  });
  const ingredients = useQuery({
    queryKey: ["ingredients-count"],
    queryFn: () => api.listIngredients({ per_page: 1 }),
  });
  const stores = useQuery({
    queryKey: ["stores-count"],
    queryFn: () => api.listStores({ per_page: 1 }),
  });

  // Action-required data (auto-refresh every 30s)
  const inventoryAlerts = useQuery({
    queryKey: ["dash-inventory-alerts"],
    queryFn: () => rawFetch<{ id: number }[]>("/api/inventory/alerts"),
    refetchInterval: 30_000,
  });
  const maintenancePredictions = useQuery({
    queryKey: ["dash-maintenance-predictions"],
    queryFn: () => rawFetch<{ probability: number }[]>("/api/maintenance/predictions"),
    refetchInterval: 30_000,
  });
  const pendingForms = useQuery({
    queryKey: ["dash-pending-forms"],
    queryFn: () => rawFetch<{ total: number }>("/api/paperless/forms?status=submitted&per_page=1"),
    refetchInterval: 30_000,
  });
  const pendingOrders = useQuery({
    queryKey: ["dash-pending-orders"],
    queryFn: () => rawFetch<{ total: number }>("/api/production/orders?status=pending&per_page=1"),
    refetchInterval: 30_000,
  });

  // System status
  const iotOverview = useQuery({
    queryKey: ["dash-iot-overview"],
    queryFn: () =>
      rawFetch<{ gateways_total: number; gateways_online: number; devices_total: number }>("/api/iot/overview"),
  });
  const equipment = useQuery({
    queryKey: ["dash-equipment"],
    queryFn: () => rawFetch<{ id: number }[]>("/api/maintenance/equipment"),
  });

  // Recent activity
  const auditLogs = useQuery({
    queryKey: ["dash-audit-logs"],
    queryFn: () => rawFetch<{ items: AuditEntry[] }>("/api/audit/logs?per_page=5"),
    refetchInterval: 30_000,
  });

  // Production throughput
  const productionOrders = useQuery({
    queryKey: ["dash-production-orders"],
    queryFn: () =>
      rawFetch<{ items: { status: string; completed_at?: string }[]; total: number }>(
        "/api/production/orders"
      ),
    refetchInterval: 30_000,
  });

  // CRM stats
  const crmStats = useQuery({
    queryKey: ["dash-crm-stats"],
    queryFn: () =>
      rawFetch<{ total_revenue: number; active_orders: number; pending_orders: number }>(
        "/api/crm/stats"
      ),
    refetchInterval: 30_000,
  });

  // Procurement stats
  const procurementStats = useQuery({
    queryKey: ["dash-procurement-stats"],
    queryFn: () =>
      rawFetch<{
        pending_count: number;
        pending_amount: number;
        avg_supplier_score: number;
      }>("/api/procurement/stats"),
    refetchInterval: 30_000,
  });

  // Equipment with health scores (richer type than system status query)
  const equipmentHealth = useQuery({
    queryKey: ["dash-equipment-health"],
    queryFn: () =>
      rawFetch<{ id: number; health_score: number }[]>("/api/maintenance/equipment"),
    refetchInterval: 30_000,
  });

  // Derived counts
  const alertCount = inventoryAlerts.data?.length;
  const predictionCount = maintenancePredictions.data
    ? maintenancePredictions.data.filter((p) => p.probability > 0.6).length
    : undefined;
  const formCount = (pendingForms.data as { total?: number } | undefined)?.total;
  const orderCount = (pendingOrders.data as { total?: number } | undefined)?.total;

  const totalActions = [alertCount, predictionCount, formCount, orderCount]
    .filter((n): n is number => n !== undefined)
    .reduce((a, b) => a + b, 0);

  const gwOnline = iotOverview.data?.gateways_online ?? 0;
  const gwTotal = iotOverview.data?.gateways_total ?? 0;
  const eqCount = equipment.data?.length ?? 0;

  const logs = auditLogs.data?.items ?? [];

  // Production throughput derived
  const prodItems = Array.isArray(productionOrders.data) ? productionOrders.data : productionOrders.data?.items ?? [];
  const now7 = new Date();
  now7.setDate(now7.getDate() - 7);
  const completedThisWeek = prodItems.filter(
    (o) => o.status === "completed" && o.completed_at && new Date(o.completed_at) >= now7
  ).length;
  const inProgressOrders = prodItems.filter((o) => o.status === "in_progress").length;

  // CRM derived
  const crmRevenue = crmStats.data?.total_revenue;
  const crmActiveOrders = crmStats.data?.active_orders;
  const crmPendingOrders = crmStats.data?.pending_orders;

  // Procurement derived
  const pendingPOs = procurementStats.data?.pending_count;
  const pendingAmount = procurementStats.data?.pending_amount;
  const avgSupplierScore = procurementStats.data?.avg_supplier_score;

  // Equipment health derived
  const healthScores = (equipmentHealth.data ?? []).map((e) => e.health_score).filter(Boolean);
  const avgHealth =
    healthScores.length > 0
      ? Math.round(healthScores.reduce((a, b) => a + b, 0) / healthScores.length)
      : undefined;

  // Pipeline status derivation
  type PipelineStatus = "green" | "yellow" | "red";
  function pipelineColor(metric: number | undefined, yellowThresh: number, redThresh: number): PipelineStatus {
    if (metric === undefined) return "yellow";
    if (metric >= redThresh) return "red";
    if (metric >= yellowThresh) return "yellow";
    return "green";
  }
  // For supplier score: higher is better, invert logic
  function scoreColor(score: number | undefined): PipelineStatus {
    if (score === undefined) return "yellow";
    if (score >= 80) return "green";
    if (score >= 60) return "yellow";
    return "red";
  }

  const pipelineSteps: { label: string; status: PipelineStatus }[] = [
    { label: "供應鏈", status: scoreColor(avgSupplierScore) },
    { label: "採購", status: pipelineColor(pendingPOs, 5, 15) },
    { label: "庫存", status: pipelineColor(alertCount, 1, 5) },
    { label: "生產", status: pipelineColor(orderCount, 3, 10) },
    { label: "品質", status: pipelineColor(predictionCount, 1, 3) },
    { label: "出貨", status: pipelineColor(crmPendingOrders, 5, 15) },
  ];

  const pipelineDotColor: Record<PipelineStatus, string> = {
    green: "bg-emerald-400",
    yellow: "bg-amber-400",
    red: "bg-red-500",
  };
  const pipelineBgColor: Record<PipelineStatus, string> = {
    green: "bg-emerald-50 border-emerald-200",
    yellow: "bg-amber-50 border-amber-200",
    red: "bg-red-50 border-red-200",
  };
  const pipelineTextColor: Record<PipelineStatus, string> = {
    green: "text-emerald-700",
    yellow: "text-amber-700",
    red: "text-red-700",
  };

  const displayName = user?.name ?? "管理者";

  return (
    <div className="max-w-6xl mx-auto flex flex-col" style={{ minHeight: 0 }}>
      {/* ── Hero Banner ── */}
      <PageHero
        image="/assets/hero-factory.jpg"
        video="/assets/hero-factory-video.mp4"
        title={`${getGreeting(now.getHours())}，${displayName}`}
        subtitle={`${formatDate(now)} ${formatTime(now)} — 奇美食品數位管理平台`}
        height="lg"
        overlay="gradient"
      />

      {/* ── Main 2-column layout ── */}
      <div className="flex gap-6 flex-1 min-h-0">
        {/* ── Left column (~60%) ── */}
        <div className="flex-[3] flex flex-col gap-6 min-w-0">
          {/* Action Required */}
          <div>
            <div className="flex items-center gap-2 mb-3 animate-fade-up" style={{ animationDelay: "80ms" }}>
              <h2 className="text-sm font-bold text-slate-700">需要處理</h2>
              {totalActions > 0 && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-qimei-500 px-1.5 text-[11px] font-bold text-white">
                  {totalActions}
                </span>
              )}
            </div>
            <div className="grid gap-2">
              <ActionCard severity="red"    icon={BarChart3}      label="庫存不足"   count={alertCount}      to="/inventory"   delay={120} />
              <ActionCard severity="yellow" icon={Wrench}         label="設備預警"   count={predictionCount} to="/maintenance" delay={180} />
              <ActionCard severity="blue"   icon={ClipboardCheck} label="待簽核"     count={formCount}       to="/paperless"   delay={240} />
              <ActionCard severity="gray"   icon={Clock}          label="待排程工單" count={orderCount}      to="/production"  delay={300} />
            </div>
          </div>

          {/* Quick Actions */}
          <div>
            <h2 className="text-sm font-bold text-slate-700 mb-3 animate-fade-up" style={{ animationDelay: "340ms" }}>
              快捷操作
            </h2>
            <div className="grid grid-cols-4 gap-2">
              <QuickAction icon={Plus}      label="新增工單" to="/production" delay={380} />
              <QuickAction icon={BarChart3} label="庫存異動" to="/inventory"  delay={420} />
              <QuickAction icon={FileStack} label="提交表單" to="/paperless"  delay={460} />
              <QuickAction icon={Upload}    label="上傳文件" to="/ocr"        delay={500} />
            </div>
          </div>

          {/* Recent Activity */}
          <div className="flex-1 min-h-0">
            <h2 className="text-sm font-bold text-slate-700 mb-3 animate-fade-up" style={{ animationDelay: "540ms" }}>
              最近活動
            </h2>
            <div className="space-y-0.5 animate-fade-up" style={{ animationDelay: "580ms" }}>
              {logs.length === 0 && (
                <p className="text-xs text-slate-400 py-2">尚無活動紀錄</p>
              )}
              {logs.map((log) => {
                const LIcon = actionIcon(log.action);
                return (
                  <div key={log.id} className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-white/80 transition-colors">
                    <span className="text-[11px] text-slate-400 tabular-nums w-16 shrink-0">
                      {relativeTime(log.created_at)}
                    </span>
                    <div className="flex h-6 w-6 items-center justify-center rounded bg-slate-100 text-slate-400 shrink-0">
                      <LIcon size={13} strokeWidth={1.5} />
                    </div>
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                      <User size={12} className="text-slate-300 shrink-0" />
                      <span className="text-xs font-medium text-slate-600 shrink-0">
                        {log.user_email ?? "系統"}
                      </span>
                      <span className="text-xs text-slate-400 truncate">
                        {log.resource_name ?? `${log.action} ${log.resource_type}`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Right column (~40%) ── */}
        <div className="flex-[2] flex flex-col gap-6 min-w-0">
          {/* KPI Stats 2x2 */}
          <div>
            <h2 className="text-sm font-bold text-slate-700 mb-3 animate-fade-up" style={{ animationDelay: "100ms" }}>
              即時狀態
            </h2>
            <div className="grid grid-cols-2 gap-2">
              <MiniStat label="產品" value={products.data?.total}     icon={Package} to="/products"     delay={140} />
              <MiniStat label="供應商" value={suppliers.data?.total}   icon={Truck}   to="/suppliers"   delay={200} />
              <MiniStat label="原料" value={ingredients.data?.total}   icon={Wheat}   to="/ingredients" delay={260} />
              <MiniStat label="通路" value={stores.data?.total}        icon={Store}   to="/stores"      delay={320} />
            </div>
          </div>

          {/* Cross-module KPI Cards */}
          <div>
            <h2 className="text-sm font-bold text-slate-700 mb-3 animate-fade-up" style={{ animationDelay: "360ms" }}>
              跨模組 KPI
            </h2>
            <div className="grid grid-cols-1 gap-2">
              {/* Production throughput */}
              <div className="flex items-center gap-3 rounded-xl border border-slate-200/60 bg-white px-4 py-3 animate-fade-up" style={{ animationDelay: "400ms" }}>
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-500">
                  <Factory size={18} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-400">生產概況（本週）</p>
                  <div className="flex items-baseline gap-3 mt-0.5">
                    <span className="text-lg font-bold text-slate-900 tabular-nums">{completedThisWeek}</span>
                    <span className="text-[11px] text-slate-400">已完成</span>
                    <span className="text-lg font-bold text-amber-600 tabular-nums">{inProgressOrders}</span>
                    <span className="text-[11px] text-slate-400">進行中</span>
                  </div>
                </div>
              </div>

              {/* CRM Revenue */}
              <div className="flex items-center gap-3 rounded-xl border border-slate-200/60 bg-white px-4 py-3 animate-fade-up" style={{ animationDelay: "440ms" }}>
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-500">
                  <DollarSign size={18} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-400">CRM 營收</p>
                  <div className="flex items-baseline gap-3 mt-0.5">
                    <span className="text-lg font-bold text-slate-900 tabular-nums">
                      {crmRevenue !== undefined
                        ? `$${(crmRevenue / 10000).toFixed(1)}萬`
                        : "—"}
                    </span>
                    <span className="text-[11px] text-slate-400">
                      {crmActiveOrders !== undefined ? `${crmActiveOrders} 筆進行中` : ""}
                    </span>
                  </div>
                </div>
              </div>

              {/* Procurement status */}
              <div className="flex items-center gap-3 rounded-xl border border-slate-200/60 bg-white px-4 py-3 animate-fade-up" style={{ animationDelay: "480ms" }}>
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-500">
                  <ShoppingCart size={18} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-400">採購狀態</p>
                  <div className="flex items-baseline gap-3 mt-0.5">
                    <span className="text-lg font-bold text-slate-900 tabular-nums">{pendingPOs ?? "—"}</span>
                    <span className="text-[11px] text-slate-400">待處理 PO</span>
                    <span className="text-sm font-semibold text-slate-600 tabular-nums">
                      {pendingAmount !== undefined ? `$${(pendingAmount / 10000).toFixed(1)}萬` : ""}
                    </span>
                  </div>
                </div>
              </div>

              {/* Equipment health gauge */}
              <div className="flex items-center gap-3 rounded-xl border border-slate-200/60 bg-white px-4 py-3 animate-fade-up" style={{ animationDelay: "520ms" }}>
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-500">
                  <Activity size={18} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-400">設備健康度</p>
                  <div className="flex items-center gap-3 mt-1">
                    <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${
                          (avgHealth ?? 0) >= 80
                            ? "bg-emerald-400"
                            : (avgHealth ?? 0) >= 60
                              ? "bg-amber-400"
                              : "bg-red-400"
                        }`}
                        style={{ width: `${avgHealth ?? 0}%` }}
                      />
                    </div>
                    <span className="text-lg font-bold text-slate-900 tabular-nums w-12 text-right">
                      {avgHealth !== undefined ? `${avgHealth}%` : "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* System Status */}
          <div className="animate-fade-up" style={{ animationDelay: "560ms" }}>
            <h2 className="text-sm font-bold text-slate-700 mb-3">系統連接</h2>
            <div className="rounded-xl border border-slate-200/60 bg-white p-4 space-y-3">
              <div className="flex items-center gap-2.5">
                <StatusDot ok />
                <span className="text-xs text-slate-600">Master API</span>
                <span className="ml-auto text-[11px] text-signal-500 font-medium">正常</span>
              </div>
              <div className="flex items-center gap-2.5">
                <StatusDot ok={gwOnline > 0} />
                <span className="text-xs text-slate-600">IoT 聚合器</span>
                <span className="ml-auto text-[11px] text-slate-500 tabular-nums font-medium">
                  {gwOnline}/{gwTotal} 在線
                </span>
              </div>
              <div className="flex items-center gap-2.5">
                <StatusDot ok={eqCount > 0} />
                <span className="text-xs text-slate-600">設備監控</span>
                <span className="ml-auto text-[11px] text-slate-500 tabular-nums font-medium">
                  {eqCount} 台運行中
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Additional stat cards row ── */}
      <div className="mt-6">
        <h2 className="text-sm font-bold text-slate-700 mb-3 animate-fade-up" style={{ animationDelay: "600ms" }}>
          補充指標
        </h2>
        <div className="grid grid-cols-3 gap-2">
          <MiniStat label="生產中工單" value={inProgressOrders} icon={Factory} to="/production" delay={640} />
          <MiniStat label="CRM 待出貨" value={crmPendingOrders} icon={DollarSign} to="/crm" delay={680} />
          <MiniStat label="採購待驗收" value={pendingPOs} icon={ShoppingCart} to="/procurement" delay={720} />
        </div>
      </div>

      {/* ── Cross-module pipeline status ── */}
      <div className="mt-6 mb-6 animate-fade-up" style={{ animationDelay: "760ms" }}>
        <h2 className="text-sm font-bold text-slate-700 mb-3">供應鏈流程狀態</h2>
        <div className="rounded-xl border border-slate-200/60 bg-white p-5">
          <div className="flex items-center justify-between gap-0">
            {pipelineSteps.map((step, i) => (
              <div key={step.label} className="flex items-center flex-1 min-w-0">
                {/* Step node */}
                <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-full border-2 ${pipelineBgColor[step.status]}`}
                  >
                    <div className={`h-3 w-3 rounded-full ${pipelineDotColor[step.status]}`} />
                  </div>
                  <span className={`text-[11px] font-semibold whitespace-nowrap ${pipelineTextColor[step.status]}`}>
                    {step.label}
                  </span>
                </div>
                {/* Connector arrow */}
                {i < pipelineSteps.length - 1 && (
                  <div className="flex-1 flex items-center justify-center -mt-5">
                    <div className="w-full h-0.5 bg-slate-200 relative">
                      <ArrowRight size={12} className="text-slate-300 absolute -right-1.5 -top-[5px]" />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-100">
            <div className="flex items-center gap-1.5">
              <div className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
              <span className="text-[11px] text-slate-400">正常</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-2.5 w-2.5 rounded-full bg-amber-400" />
              <span className="text-[11px] text-slate-400">需關注</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-2.5 w-2.5 rounded-full bg-red-500" />
              <span className="text-[11px] text-slate-400">異常</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
